// src/hooks/order/useOrderProducts.ts
// Hook para manejo de productos en OrderForm
// Basado en useCotizacionProducts pero adaptado para OrderForm

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import toast from 'react-hot-toast'
import axiosInstance from '../../api/axiosInstance'
import type { OrderData, Product, LineItem } from '../../types/orderFormTypes'
import { calculateTotals as calculateTotalsUtil } from '../../utils/orderFormUtils'
import {
  isClienteNetoActive,
  applyClienteNetoToLineItems,
  lineItemSku
} from '../../utils/clienteNetoOrder'
import { useClienteNetoProductSync } from './useClienteNetoProductSync'
import { useProductSearch } from '../useProductSearch'
import { isProductInactiveInSearch } from '../../utils/productSearchUtils'
import {
  getOrderStockDisplayOptions,
  mapApiProductToOrderDetails,
  reapplyOrderProductDetailsStock
} from '../../utils/productStockDisplayUtils'
import type { BodegaRecogidaSeleccion } from './useRecogeEnBodega'

interface UseOrderProductsProps {
  formData: OrderData
  setFormData: React.Dispatch<React.SetStateAction<OrderData>>
  mode: 'view' | 'edit' | 'create'
  costoEnvio: string
  recogeEnBodega: boolean
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
}

export function useOrderProducts({
  formData,
  setFormData,
  mode,
  costoEnvio,
  recogeEnBodega,
  bodegaRecogidaSeleccion
}: UseOrderProductsProps) {
  const stockDisplayOptions = useMemo(
    () => getOrderStockDisplayOptions(recogeEnBodega, bodegaRecogidaSeleccion),
    [recogeEnBodega, bodegaRecogidaSeleccion]
  )

  const [productDetails, setProductDetails] = useState<{ [key: string]: any }>({})
  const [expandedCombos, setExpandedCombos] = useState<{ [key: string]: boolean }>(() => {
    const initialExpanded: { [key: string]: boolean } = {}
    if (formData.lineItems) {
      formData.lineItems.forEach(item => {
        initialExpanded[item.sku || item.product_id] = true
      })
    }
    return initialExpanded
  })
  const [loadingDetails, setLoadingDetails] = useState<{ [key: string]: boolean }>({})
  
  // Refs para evitar múltiples cargas
  const lastLoadedSkusRef = useRef<string>('')
  const isLoadingRef = useRef<boolean>(false)

  const { markLineItemsCnApplied, unmarkLineItemCnApplied } = useClienteNetoProductSync({
    mode,
    formData,
    productDetails,
    costoEnvio,
    setFormData
  })

  // Hook para búsqueda de productos (ya existe)
  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    clearSearch
  } = useProductSearch()

  // Función para cargar detalles de productos
  const loadProductDetails = useCallback(async () => {
    if (!formData.lineItems?.length) {
      return
    }

    // Generar clave única basada en los SKUs y status
    const estadosQueVerificanStock = ['processing', 'pending', 'send_later', 'verify_transaction']
    const agregarSinSaldo = !estadosQueVerificanStock.includes(formData.status || '')
    const allSkus: string[] = []
    
    formData.lineItems.forEach(item => {
      allSkus.push(item.sku || item.product_id)
    })
    
    formData.lineItems.forEach(item => {
      if (item.is_combo === 1 && item.components) {
        item.components.forEach((component: any) => {
          allSkus.push(component.sku)
        })
      }
    })
    
    const uniqueSkus: string[] = Array.from(new Set(allSkus))
    const skusKey = `${uniqueSkus.sort().join(',')}:${formData.status || ''}:${agregarSinSaldo}`
    
    // Evitar consultas duplicadas
    if (isLoadingRef.current || lastLoadedSkusRef.current === skusKey) {
      console.log('⚠️ Productos ya cargados o cargando, omitiendo carga duplicada')
      return
    }
    
    try {
      isLoadingRef.current = true
      lastLoadedSkusRef.current = skusKey
      
      const newDetails: { [key: string]: any } = {}
      const newLoadingDetails: { [key: string]: boolean } = {}
      uniqueSkus.forEach(sku => {
        newLoadingDetails[sku] = true
        setLoadingDetails(prev => ({...prev, [sku]: true}))
      })
      
      try {
        let multipleUrl = `/products/multiple?skus=${uniqueSkus.join(',')}`
        if (agregarSinSaldo) {
          multipleUrl += "&SinSaldo=true"
        }
        
        const token = localStorage.getItem('accessToken')
        const response = await axiosInstance.get(multipleUrl, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.data && response.data.products) {
          // Procesar cada producto de la respuesta
          response.data.products.forEach((product: any) => {
            const mapped = mapApiProductToOrderDetails(product, stockDisplayOptions)
            newDetails[mapped.sku] = mapped
          })
        }

        // Procesar combos para calcular stock mínimo
        formData.lineItems.forEach(item => {
          if (item.is_combo === 1 && item.components) {
            const stocks: number[] = []
            item.components.forEach((component: any) => {
              const componentDetails = newDetails[component.sku]
              if (componentDetails) {
                const stockValue = componentDetails.stock || componentDetails.stock_quantity || 0
                stocks.push(stockValue)
              } else {
                stocks.push(0)
              }
            })
            
            // Actualizar stock del combo con el mínimo
            if (stocks.length > 0) {
              const minStock = Math.min(...stocks)
              if (newDetails[item.sku || item.product_id]) {
                newDetails[item.sku || item.product_id] = {
                  ...newDetails[item.sku || item.product_id],
                  stock: minStock,
                  stock_quantity: minStock
                }
              }
            }
          }
        })
        
      } catch (error) {
        console.error('Error al cargar productos múltiples:', error)
        
        // Fallback: consultas individuales
        for (const item of formData.lineItems) {
          let response
          try {
            newLoadingDetails[item.sku || item.product_id] = true
            setLoadingDetails(prev => ({ ...prev, [item.sku || item.product_id]: true }))
            
            const token = localStorage.getItem('accessToken')
            response = await axiosInstance.get(`/products/${item.sku || item.product_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
            
            if (response.data) {
              const mapped = mapApiProductToOrderDetails(response.data, stockDisplayOptions)
              newDetails[item.sku || item.product_id] = mapped
              
              // Procesar componentes si es un combo
              if (response.data.is_combo === 1 && response.data.components) {
                const stocks: number[] = []
                
                for (const component of response.data.components) {
                  const compSku = component.sku
                  // Usar newDetails en lugar de productDetails para evitar dependencias
                  if (!newDetails[compSku] && compSku) {
                    setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
                    
                    try {
                      const componentResponse = await axiosInstance.get(`/products/${compSku}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                      
                      if (componentResponse.data) {
                        const mappedComp = mapApiProductToOrderDetails(
                          componentResponse.data,
                          stockDisplayOptions
                        )
                        stocks.push(mappedComp.stock)
                        newDetails[compSku] = mappedComp
                      }
                    } catch (componentError) {
                      console.error('Error al cargar componente:', compSku, componentError)
                      stocks.push(0)
                    } finally {
                      setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
                    }
                  } else if (newDetails[compSku]) {
                    const stockValue = newDetails[compSku].stock || newDetails[compSku].stock_quantity || 0
                    stocks.push(stockValue)
                  }
                }
                
                // Calcular stock mínimo para el combo
                if (stocks.length > 0) {
                  const minStock = Math.min(...stocks)
                  newDetails[item.sku || item.product_id] = {
                    ...newDetails[item.sku || item.product_id],
                    stock: minStock,
                    stock_quantity: minStock
                  }
                }
              }
            }
          } catch (err) {
            console.error('Error al cargar producto individual:', item.sku || item.product_id, err)
            newDetails[item.sku || item.product_id] = {
              ...response?.data,
              stock: 0,
              precio_ofima: null,
              iva_ofima: null,
              price: 0,
              tax_rate: 0.19
            }
          } finally {
            newLoadingDetails[item.sku || item.product_id] = false
            setLoadingDetails(prev => ({ ...prev, [item.sku || item.product_id]: false }))
          }
        }
      } finally {
        // Marcar todos como no cargando
        uniqueSkus.forEach(sku => {
          newLoadingDetails[sku] = false
          setLoadingDetails(prev => ({...prev, [sku]: false}))
        })
        isLoadingRef.current = false
      }
      setProductDetails(newDetails)
    } catch (error) {
      console.error('Error en loadProductDetails:', error)
      isLoadingRef.current = false
    }
  }, [formData.lineItems, formData.status, stockDisplayOptions])

  useEffect(() => {
    setProductDetails(prev => {
      if (!prev || Object.keys(prev).length === 0) return prev
      return reapplyOrderProductDetailsStock(prev, formData.lineItems, stockDisplayOptions)
    })
  }, [stockDisplayOptions, formData.lineItems])

  // useEffect para cargar productos
  useEffect(() => {
    // Solo cargar si hay lineItems y no se está cargando actualmente
    if (formData.lineItems && formData.lineItems.length > 0 && !isLoadingRef.current) {
      // Generar clave única para verificar si ya se cargaron estos productos
      const estadosQueVerificanStock = ['processing', 'pending', 'send_later', 'verify_transaction']
      const agregarSinSaldo = !estadosQueVerificanStock.includes(formData.status || '')
      const allSkus: string[] = []
      
      formData.lineItems.forEach(item => {
        allSkus.push(item.sku || item.product_id)
      })
      
      formData.lineItems.forEach(item => {
        if (item.is_combo === 1 && item.components) {
          item.components.forEach((component: any) => {
            allSkus.push(component.sku)
          })
        }
      })
      
      const uniqueSkus: string[] = Array.from(new Set(allSkus))
      const skusKey = `${uniqueSkus.sort().join(',')}:${formData.status || ''}:${agregarSinSaldo}`
      
      // Solo cargar si la clave es diferente a la última cargada
      if (lastLoadedSkusRef.current !== skusKey) {
        loadProductDetails()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.lineItems?.length, formData.status])

  // Calcular totales
  const calculateTotals = useCallback((items: LineItem[] = []) => {
    return calculateTotalsUtil(
      items,
      productDetails,
      costoEnvio,
      isClienteNetoActive(formData.extraDetails),
      mode === 'view'
    )
  }, [productDetails, costoEnvio, formData.extraDetails?.cliente_neto, mode])

  // Seleccionar producto
  const handleSelectProduct = useCallback((selectedProduct: Product) => {
    if (isProductInactiveInSearch(selectedProduct)) {
      toast.error('Este producto está inactivo y no se puede agregar al pedido', { icon: '⚠️' })
      return
    }
    const newLineItems = [...(formData.lineItems || [])]
    const existingItemIndex = newLineItems.findIndex(item => item.sku === selectedProduct.sku)
    
    if (existingItemIndex >= 0) {
      // Si el producto ya existe, incrementar la cantidad
      newLineItems[existingItemIndex].quantity += 1
    } else {
      // Si es un nuevo producto, agregarlo
      const basePrice = Number(selectedProduct.price || 0)
      // Cliente neto: precio de catálogo sin sumar IVA encima
      const newItem: any = {
        product_id: selectedProduct.sku,
        product_name: selectedProduct.name,
        quantity: 1,
        price: basePrice.toString()
      }

      // Si es un combo, agregar información de combo y componentes
      if (selectedProduct.is_combo) {
        newItem.is_combo = selectedProduct.is_combo
        newItem.components = selectedProduct.components
        
        // Desplegarlo automáticamente
        setExpandedCombos(prev => ({
          ...prev,
          [selectedProduct.sku]: true
        }))
      }

      newLineItems.push(newItem)

      if (isClienteNetoActive(formData.extraDetails)) {
        const sku = lineItemSku(newItem)
        const idx = newLineItems.length - 1
        const [withNeto] = applyClienteNetoToLineItems(
          [newLineItems[idx]],
          productDetails,
          [sku]
        )
        newLineItems[idx] = withNeto
        markLineItemsCnApplied([newLineItems[idx]])
      }
    }

    const totals = calculateTotals(newLineItems)
    
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems,
      total: totals.total
    }))
    
    clearSearch()
  }, [
    formData.lineItems,
    formData.extraDetails?.cliente_neto,
    calculateTotals,
    clearSearch,
    setFormData,
    productDetails,
    markLineItemsCnApplied
  ])

  // Cambiar cantidad
  const handleQuantityChange = useCallback((index: number, newQuantity: number) => {
    const newLineItems = [...(formData.lineItems || [])]
    if (newQuantity >= 1) {
      newLineItems[index].quantity = newQuantity
    }

    const totals = calculateTotals(newLineItems)
    
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems,
      total: totals.total
    }))
  }, [formData.lineItems, calculateTotals, setFormData])

  // Eliminar producto
  const handleRemoveProduct = useCallback((index: number) => {
    const newLineItems = [...(formData.lineItems || [])]
    const removed = newLineItems[index]
    newLineItems.splice(index, 1)
    if (removed) {
      unmarkLineItemCnApplied(lineItemSku(removed))
    }

    const totals = calculateTotals(newLineItems)
    
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems,
      total: totals.total
    }))
  }, [formData.lineItems, calculateTotals, setFormData, unmarkLineItemCnApplied])

  // Cambiar descuento
  const handleDiscountChange = useCallback((index: number, newDiscount: number) => {
    const newLineItems = [...(formData.lineItems || [])]
    newLineItems[index].discount = newDiscount

    const totals = calculateTotals(newLineItems)
    
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems,
      total: totals.total
    }))
  }, [formData.lineItems, calculateTotals, setFormData])

  // Cambiar precio
  const handlePriceChange = useCallback((index: number, newPrice: number) => {
    const newLineItems = [...(formData.lineItems || [])]
    newLineItems[index].price = newPrice.toString()

    const totals = calculateTotals(newLineItems)
    
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems,
      total: totals.total
    }))
  }, [formData.lineItems, calculateTotals, setFormData])

  // Toggle combo
  const toggleCombo = useCallback(async (productId: string) => {
    setExpandedCombos(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }))

    // Si se está expandiendo el combo, cargar los detalles de los componentes
    if (!expandedCombos[productId]) {
      const productDetail = productDetails[productId]
      if (productDetail?.is_combo === 1 && productDetail?.components) {
        const stocks: number[] = []
        
        // Cargar detalles de cada componente
        for (const component of productDetail.components) {
          const compSku = component.sku
          if (!productDetails[compSku] && compSku) {
            setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
            try {
              const token = localStorage.getItem('accessToken')
              const response = await axiosInstance.get(`/products/${compSku}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              if (response.data) {
                const mappedComp = mapApiProductToOrderDetails(response.data)
                stocks.push(mappedComp.stock)
                setProductDetails(prev => ({
                  ...prev,
                  [compSku]: mappedComp
                }))
              }
            } catch (error) {
              console.error('Error al cargar el componente:', compSku, error)
              stocks.push(0)
            } finally {
              setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
            }
          } else if (productDetails[compSku]) {
            const stockValue = productDetails[compSku].stock || productDetails[compSku].stock_quantity || 0
            stocks.push(stockValue)
          }
        }

        // Una vez que tenemos todos los stocks, actualizamos el producto principal
        if (stocks.length > 0) {
          const minStock = Math.min(...stocks)
          
          setProductDetails(prev => ({
            ...prev,
            [productId]: {
              ...prev[productId],
              stock: minStock,
              stock_quantity: minStock
            }
          }))
        }
      }
    }
  }, [expandedCombos, productDetails])

  return {
    // Búsqueda
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    clearSearch,
    
    // Productos
    productDetails,
    expandedCombos,
    loadingDetails,
    
    // Handlers
    handleSelectProduct,
    handleQuantityChange,
    handleDiscountChange,
    handlePriceChange,
    handleRemoveProduct,
    toggleCombo,
    
    // Cálculos
    calculateTotals,

    markLineItemsCnApplied
  }
}

